import React, { useEffect, useState } from "react";
import "../styles/clients.css";  // base styles
import "../styles/users.css";    // agency multiselect + overrides
import { Plus, Edit, Trash2, RefreshCw, X, XCircle } from "lucide-react";
import { useGeneric } from "../hooks/useGeneric";

const EMPTY_FORM = {
    user_id: null,
    user_name: "",
    mobile_number: "",
    password: "",
    device_id: "",
    agency_id: [],
};

const UserScreen = () => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [showForm, setShowForm] = useState(false);
    const [errors, setErrors] = useState({});
    const [search, setSearch] = useState("");
    const [saving, setSaving] = useState(false);

    const { data: users = [], loading, fetchAll: fetchUsers, save: saveUser, remove: deleteUser } = useGeneric({
        tableName: "users",
        idColumn: "user_id",
        orderBy: "user_name",
        removeOnInsert: ["user_id"],
    });

    const { data: agencies = [], fetchAll: fetchAgencies } = useGeneric({
        tableName: "agencies",
        idColumn: "agency_id",
        orderBy: "agency_name",
    });

    useEffect(() => {
        fetchUsers();
        fetchAgencies();
    }, [fetchUsers, fetchAgencies]);

    const set = (key, val) => {
        setForm((prev) => ({ ...prev, [key]: val }));
        setErrors((prev) => ({ ...prev, [key]: "" }));
    };

    // ── Multi-select agency toggle ───────────────────────────────────
    const toggleAgency = (agencyId) => {
        const id = Number(agencyId);
        setForm((prev) => {
            const current = (prev.agency_id ?? []).map(Number);
            const already = current.includes(id);
            return {
                ...prev,
                agency_id: already
                    ? current.filter((a) => a !== id)
                    : [...current, id],
            };
        });
        setErrors((prev) => ({ ...prev, agency_id: "" }));
    };

    // ── Validation ───────────────────────────────────────────────────
    const validate = () => {
        const errs = {};

        if (!form.user_name.trim())
            errs.user_name = "User name is required.";
        else if (form.user_name.length > 50)
            errs.user_name = "Max 50 characters.";

        if (!form.mobile_number.trim())
            errs.mobile_number = "Mobile number is required.";
        else if (!/^\d{10}$/.test(form.mobile_number.trim()))
            errs.mobile_number = "Mobile must be 10 digits.";

        if (!form.password.trim())
            errs.password = "Password is required.";

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ── Save ─────────────────────────────────────────────────────────
    const onSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const payload = { ...form };
            if (!payload.user_id) delete payload.user_id;
            await saveUser(form);
            closeModal();
        } catch (err) {
            console.error("Save failed", err);
            alert("Save failed: " + (err.message || "Unknown error"));
        } finally {
            setSaving(false);
        }
    };

    // ── Modal helpers ────────────────────────────────────────────────
    const openAdd = () => {
        setForm(EMPTY_FORM);
        setErrors({});
        setShowForm(true);
    };

    const openEdit = (user) => {
        let agencyIds = user.agency_id ?? [];
        if (typeof agencyIds === "string") {
            try { agencyIds = JSON.parse(agencyIds); }
            catch { agencyIds = []; }
        }
        if (!Array.isArray(agencyIds)) agencyIds = [];
        setForm({
            ...user,
            agency_id: agencyIds.map(Number),
        });
        setErrors({});
        setShowForm(true);
    };

    const closeModal = () => {
        setShowForm(false);
        setForm(EMPTY_FORM);
        setErrors({});
    };

    // ── Delete ───────────────────────────────────────────────────────
    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete this user?")) {
            deleteUser(id);
        }
    };

    // ── Filter ───────────────────────────────────────────────────────
    const filtered = (users || [])
        .filter(
            (u) =>
                u.user_name?.toLowerCase().includes(search.toLowerCase()) ||
                u.mobile_number?.includes(search)
        )
        .sort((a, b) => a.user_name?.localeCompare(b.user_name));

    return (
        <div className="cl-page">
            {/* ── Panel ── */}
            <div className="cl-panel">
                <div className="cl-panel-heading">
                    <span>User Details</span>
                    <button className="cl-add-btn" onClick={openAdd} title="Add User">
                        <Plus size={16} />
                    </button>
                </div>

                {/* Search bar */}
                <div className="cl-search-bar">
                    <input
                        type="text"
                        placeholder="Search users…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="cl-search-input"
                    />
                    <button className="cl-refresh-btn" onClick={fetchUsers} title="Refresh">
                        <RefreshCw size={15} />
                    </button>
                </div>

                {/* Table */}
                {loading ? (
                    <div className="cl-loading">Loading…</div>
                ) : (
                    <div className="cl-table-wrapper">
                        <table className="cl-table">
                            <thead>
                                <tr>
                                    <th>User ID</th>
                                    <th>User Name</th>
                                    <th>Mobile Number</th>
                                    <th>Device ID</th>
                                    <th className="cl-actions-th">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="cl-empty">
                                            No users found.
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map((u, i) => (
                                        <tr
                                            key={u.user_id ?? i}
                                            className={i % 2 === 0 ? "cl-row-even" : "cl-row-odd"}
                                        >
                                            <td>{u.user_id}</td>
                                            <td>{u.user_name}</td>
                                            <td>{u.mobile_number}</td>
                                            <td>{u.device_id}</td>
                                            <td className="cl-actions-td">
                                                <button
                                                    className="cl-icon-btn cl-edit"
                                                    onClick={() => openEdit(u)}
                                                    title="Edit"
                                                >
                                                    <Edit size={15} />
                                                </button>
                                                <button
                                                    className="cl-icon-btn cl-delete"
                                                    onClick={() => handleDelete(u.user_id)}
                                                    title="Delete"
                                                >
                                                    <Trash2 size={15} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="cl-panel-footer" />
            </div>

            {/* ── Modal ── */}
            {showForm && (
                <div className="cl-modal-backdrop">
                    <div className="cl-modal-overlay" onClick={closeModal} />
                    <div className="cl-modal" onClick={(e) => e.stopPropagation()}>

                        {/* Modal Header */}
                        <div className="cl-modal-header">
                            <h4 className="cl-modal-title">
                                {form.user_id ? "Edit User" : "Add User"}
                            </h4>
                            <button className="cl-modal-close" onClick={closeModal}>
                                <X size={18} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="cl-modal-body">
                            <form autoComplete="off">

                                {/* Row: User Name | User ID */}
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-10">
                                        <label className="cl-control-label">User Name</label>
                                        <input
                                            type="text"
                                            className={`cl-form-control${errors.user_name ? " cl-input-error" : ""}`}
                                            placeholder="User Name"
                                            maxLength={50}
                                            value={form.user_name}
                                            onChange={(e) => set("user_name", e.target.value)}
                                        />
                                        {errors.user_name && (
                                            <span className="cl-error-msg">{errors.user_name}</span>
                                        )}
                                    </div>
                                    <div className="cl-col-2">
                                        <label className="cl-control-label">User ID</label>
                                        <input
                                            type="text"
                                            className="cl-form-control cl-readonly"
                                            placeholder="Auto"
                                            value={form.user_id ?? ""}
                                            readOnly
                                        />
                                    </div>
                                </div>

                                {/* Row: Mobile Number | Password */}
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-6">
                                        <label className="cl-control-label">Mobile Number</label>
                                        <input
                                            type="tel"
                                            className={`cl-form-control${errors.mobile_number ? " cl-input-error" : ""}`}
                                            placeholder="Mobile Number"
                                            maxLength={10}
                                            value={form.mobile_number}
                                            onChange={(e) =>
                                                set("mobile_number", e.target.value.replace(/\D/g, ""))
                                            }
                                        />
                                        {errors.mobile_number && (
                                            <span className="cl-error-msg">{errors.mobile_number}</span>
                                        )}
                                    </div>
                                    <div className="cl-col-6">
                                        <label className="cl-control-label">Password</label>
                                        <input
                                            type="password"
                                            className={`cl-form-control${errors.password ? " cl-input-error" : ""}`}
                                            placeholder="Password"
                                            maxLength={50}
                                            value={form.password}
                                            onChange={(e) => set("password", e.target.value)}
                                        />
                                        {errors.password && (
                                            <span className="cl-error-msg">{errors.password}</span>
                                        )}
                                    </div>
                                </div>

                                {/* Row: Device ID with Clear button */}
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-12">
                                        <label className="cl-control-label">Device ID</label>
                                        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                                            <input
                                                type="text"
                                                className="cl-form-control cl-readonly"
                                                placeholder="Device ID (auto-assigned)"
                                                value={form.device_id ?? ""}
                                                readOnly
                                            />
                                            <button
                                                type="button"
                                                className="cl-refresh-icon-btn"
                                                onClick={() => set("device_id", "")}
                                                title="Clear Device ID"
                                                style={{ color: "#dc3545", borderColor: "#dc3545" }}
                                            >
                                                <XCircle size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Row: Agency Multi-Select */}
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-12">
                                        <label className="cl-control-label">
                                            Agencies
                                            <span style={{ fontWeight: 400, color: "#6b7280", marginLeft: 6, fontSize: 12 }}>
                                                (select one or more)
                                            </span>
                                        </label>

                                        {/* ↓ Added cl-agency-multiselect-row for mobile stacking */}
                                        <div
                                            className="cl-agency-multiselect-row"
                                            style={{ display: "flex", gap: "6px", alignItems: "flex-start" }}
                                        >
                                            <div className="cl-agency-multiselect">
                                                {agencies.length === 0 ? (
                                                    <div className="cl-agency-empty">No agencies available</div>
                                                ) : (
                                                    agencies
                                                        .slice()
                                                        .sort((a, b) => a.agency_name?.localeCompare(b.agency_name))
                                                        .map((agency) => {
                                                            const isSelected = (form.agency_id ?? [])
                                                                .map(Number)
                                                                .includes(Number(agency.agency_id));
                                                            return (
                                                                <div
                                                                    key={agency.agency_id}
                                                                    className={`cl-agency-option${isSelected ? " cl-agency-selected" : ""}`}
                                                                    onClick={() => toggleAgency(agency.agency_id)}
                                                                >
                                                                    <span className="cl-agency-check">
                                                                        {isSelected ? "✓" : ""}
                                                                    </span>
                                                                    <span>{agency.agency_name}</span>
                                                                </div>
                                                            );
                                                        })
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                className="cl-refresh-icon-btn"
                                                onClick={fetchAgencies}
                                                title="Refresh Agencies"
                                            >
                                                <RefreshCw size={14} />
                                            </button>
                                        </div>

                                        {errors.agency_id && (
                                            <span className="cl-error-msg">{errors.agency_id}</span>
                                        )}

                                        {/* Selected agency tags */}
                                        {(form.agency_id ?? []).length > 0 && (
                                            <div className="cl-agency-tags">
                                                {form.agency_id.map((id) => {
                                                    const agency = agencies.find(
                                                        (ag) => Number(ag.agency_id) === Number(id)
                                                    );
                                                    return (
                                                        <span key={id} className="cl-agency-tag">
                                                            {agency?.agency_name ?? id}
                                                            <button
                                                                type="button"
                                                                className="cl-agency-tag-remove"
                                                                onClick={() => toggleAgency(id)}
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>

                            </form>
                        </div>

                        {/* Modal Footer */}
                        <div className="cl-modal-footer">
                            <button className="cl-btn cl-btn-default" onClick={closeModal}>
                                Close
                            </button>
                            <button
                                className="cl-btn cl-btn-primary"
                                onClick={onSave}
                                disabled={saving}
                            >
                                {saving ? "Saving…" : form.user_id ? "Update" : "Add"}
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
};

export default UserScreen;